import React from 'react';
import { Button } from '@components';
import { HiOutlineClipboardList, HiOutlinePencil } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';

export type OrderFlow = 'product-list' | 'custom-offer';

interface PublicFlowSelectionProps {
    onFlowSelect: (flow: OrderFlow) => void;
    hasProducts: boolean;
    config?: any;
}

const PublicFlowSelection: React.FC<PublicFlowSelectionProps> = ({ onFlowSelect, hasProducts, config }) => {
    const { t } = useTranslation(['order_form']);
    

    
    const cssVariables = {
        '--public-form-primary': config?.primaryColor || '#7214FF',
        '--public-form-primary-light': config?.primaryColorLight || '#F1E8FF',
        '--public-form-primary-dark': config?.primaryColorDark || '#5B0ACD'
    };

    return (
        <div 
            className="min-h-screen bg-gray-100 flex flex-col"
            style={cssVariables as React.CSSProperties}
        >
            {/* Fixed Logo Header */}
            {config?.logoUrl && config.logoUrl.trim() !== "" && (
                <div className="fixed top-0 left-0 right-0 z-50 border-b border-gray-200" style={{ backgroundColor: config?.headerColor || '#FFFFFF' }}>
                    <div className="max-w-7xl mx-auto px-4 py-3">
                        <div className="flex justify-start">
                            {config.logoRedirectUrl && config.logoRedirectUrl.trim() !== "" ? (
                                <a href={config.logoRedirectUrl}>
                                    <img
                                        src={config.logoUrl}
                                        alt="Company logo"
                                        className="h-12 max-w-48 object-contain"
                                        style={{ cursor: 'pointer' }}
                                    />
                                </a>
                            ) : (
                                <img
                                    src={config.logoUrl}
                                    alt="Company logo"
                                    className="h-12 max-w-48 object-contain"
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

            
            <div className={`flex-1 flex items-center justify-center mt-8 pb-8 ${config?.logoUrl && config.logoUrl.trim() !== "" ? 'pt-20' : ''}`}>
            <style>
                {`
                    .public-form-primary-bg {
                        background-color: var(--public-form-primary);
                    }
                    .public-form-primary-light-bg {
                        background-color: var(--public-form-primary-light);
                    }
                    .public-form-primary-text {
                        color: var(--public-form-primary);
                    }
                `}
            </style>
            <div className="max-w-7xl w-full px-4">


                <div className="grid md:grid-cols-2 gap-6">
                    {/* Product List Flow */}
                    <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-transparent">
                        <div className="text-center">
                            <div className="public-form-primary-light-bg p-4 rounded-full mb-6 inline-block">
                                <HiOutlineClipboardList className="public-form-primary-text h-12 w-12" />
                            </div>
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                                {config?.productListFlowTitle || t('public_form.flow_selection.product_list.title')}
                            </h2>
                            <p className="text-gray-600 mb-6">
                                {config?.productListFlowDescription || t('public_form.flow_selection.product_list.description')}
                            </p>
                            <Button
                                size="xl"
                                onClick={() => onFlowSelect('product-list')}
                                disabled={!hasProducts}
                                className="w-full bg-white hover:bg-gray-50 text-gray-800 border border-gray-300 transition-colors"
                            >
                                {hasProducts ? t('public_form.flow_selection.product_list.button') : t('public_form.flow_selection.product_list.no_products')}
                            </Button>
                            {!hasProducts && (
                                <p className="text-sm text-gray-500 mt-2">
                                    {t('public_form.flow_selection.product_list.no_products_description')}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Custom Order Flow */}
                    <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-transparent">
                        <div className="text-center">
                            <div className="bg-gray-100 p-4 rounded-full mb-6 inline-block">
                                <HiOutlinePencil className="text-gray-600 h-12 w-12" />
                            </div>
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                                {config?.customOrderFlowTitle || t('public_form.flow_selection.custom_offer.title')}
                            </h2>
                            <p className="text-gray-600 mb-6">
                                {config?.customOrderFlowDescription || t('public_form.flow_selection.custom_offer.description')}
                            </p>
                            <Button
                                size="xl"
                                onClick={() => onFlowSelect('custom-offer')}
                                className="w-full bg-white hover:bg-gray-50 text-gray-800 border border-gray-300 transition-colors"
                            >
                                {t('public_form.flow_selection.custom_offer.button')}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </div>
    );
};

export default PublicFlowSelection; 